import React, { useState, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { FaArrowLeft, FaCheckCircle, FaClock, FaExclamationTriangle } from 'react-icons/fa';
import { getJobDetail } from '../api/api';
import { ContactTable } from '../components/ContactTable';
import RefreshButton from '../components/RefreshButton';

const JobDetail = ({ setAlert }) => {
  const { jobId } = useParams();
  const history = useHistory();

  const [metadata, setMetadata] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [calls, setCalls] = useState([]);
  const [callsNextToken, setCallsNextToken] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Load job details
  const loadJobDetail = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getJobDetail(jobId);
      setMetadata(data.metadata);
      setStatistics(data.statistics);
      setCalls(data.calls || []);
      setCallsNextToken(data.callsNextToken || null);
      setHasLoaded(true);
      console.log("DATA: ", data)
    } catch (err) {
      console.error('Error loading job details:', err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [jobId]);

  // Load more calls
  const loadMoreCalls = useCallback(async () => {
    if (!callsNextToken || isLoadingMore) return;

    setIsLoadingMore(true);
    try {
      const data = await getJobDetail(jobId, { callsNextToken });
      setCalls(prev => [...prev, ...(data.calls || [])]);
      setCallsNextToken(data.callsNextToken || null);
    } catch (err) {
      console.error('Error loading more calls:', err);
      setAlert({
        type: 'error',
        message: 'Failed to load more calls'
      });
    } finally {
      setIsLoadingMore(false);
    }
  }, [jobId, callsNextToken, isLoadingMore, setAlert]);

  // Load job details on mount
  React.useEffect(() => {
    if (!hasLoaded && jobId) {
      loadJobDetail();
    }
  }, [hasLoaded, jobId, loadJobDetail]);

  // Refresh function
  const mutate = useCallback(() => {
    loadJobDetail();
  }, [loadJobDetail]);

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading job details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => history.push('/jobs')}
          className="btn-outline flex items-center"
        >
          <FaArrowLeft className="mr-2" />
          Back to Jobs
        </button>
        <div className="card bg-red-50">
          <p className="text-red-700">Failed to load job details. Please try again.</p>
        </div>
      </div>
    );
  }

  if (!metadata) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => history.push('/jobs')}
          className="btn-outline flex items-center"
        >
          <FaArrowLeft className="mr-2" />
          Back to Jobs
        </button>
        <div className="card bg-yellow-50">
          <p className="text-yellow-700">Job not found.</p>
        </div>
      </div>
    );
  }

  const exportResults = (format) => {
    if (callsNextToken) {
      const confirmExport = window.confirm(
        'Are you sure you want to export without loading all calls?'
      );
      
      if (!confirmExport) {
        return;
      }
    }
    if (format === 'csv') {
      const headers = ['Timestamp', 'Job Name', 'Agent', 'Campaign', 'Template', 'QA Score', 'Compliance', 'Duration', 'GUID', 'Conversation ID', 'DNIS'];
      const rows = calls.map(item => [
        new Date(item.timestamp).toISOString(),
        item.jobName || '',
        item.agent || '',
        item.customer || '',
        item.template || '',
        item.Result?.QA_Score ?? 'N/A',
        item.Result?.Compliance ? 'Pass' : 'Fail',
        item.duration || '',
        item.guid || '',
        item.conversationId || '',
        item.dnis || ''
      ]);
      const csv = [headers, ...rows].map(row => row.join(',')).join('\n');

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `search-results-${Date.now()}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } else if (format === 'json') {
      const json = JSON.stringify(calls, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `search-results-${Date.now()}.json`;
      link.click();
      URL.revokeObjectURL(url);
    }
  };

  console.log("Calls for export: ", calls)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => history.push('/jobs')}
            className="btn-outline flex items-center"
          >
            <FaArrowLeft className="mr-2" />
            Back
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Job Details</h1>
            <p className="text-gray-600 mt-1">
              {jobId === 'legacy' ? 'Legacy Job' : `Job ID: ${jobId}`}
            </p>
          </div>
        </div>
        <RefreshButton mutate={mutate} />
      </div>

      {/* Job Metadata */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Job Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-gray-600">Upload Date</p>
            <p className="text-base font-medium text-gray-900">
              {formatDate(metadata.uploadDate)}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Campaign</p>
            <p className="text-base font-medium text-gray-900">{metadata.campaign}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Template</p>
            <p className="text-base font-medium text-gray-900">{metadata.template}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Uploaded By</p>
            <p className="text-base font-medium text-gray-900">{metadata.uploadedBy}</p>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Calls</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.totalCalls}</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-green-600">{statistics.completedCount}</p>
            </div>
            <FaCheckCircle className="text-3xl text-green-600" />
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Processing</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.processingCount}</p>
            </div>
            <FaClock className="text-3xl text-blue-600" />
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Failed</p>
              <p className="text-2xl font-bold text-red-600">{statistics.failedCount}</p>
            </div>
            <FaExclamationTriangle className="text-3xl text-red-600" />
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Completion</p>
              <p className="text-2xl font-bold text-cyan-600">{statistics.completionPercentage}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Average Score */}
      {statistics.averageScore !== null && (
        <div className="card bg-gradient-to-r from-cyan-50 to-blue-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Average QA Score</p>
              <p className="text-3xl font-bold text-cyan-600">{statistics.averageScore}</p>
            </div>
          </div>
        </div>
      )}

      {/* Calls Table */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Calls in this Job</h2>
        <ContactTable
          data={calls}
          setAlert={setAlert}
          showJobId={false}
          header={
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Search Results ({calls.length})
              </h2>
              <div className="flex space-x-2" style={{paddingLeft: '10px'}}>
                <button
                  onClick={() => exportResults('csv')}
                  className="btn-outline"
                >
                  Export CSV
                </button>
                <button
                  onClick={() => exportResults('json')}
                  className="btn-outline"
                >
                  Export JSON
                </button>
              </div>
            </div>
          }
        />

        {/* Load More Calls Button */}
        {callsNextToken && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 text-center">
            <button
              onClick={loadMoreCalls}
              disabled={isLoadingMore}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoadingMore ? (
                <>
                  <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Loading...
                </>
              ) : (
                'Load More Calls'
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobDetail;
